import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Spinner, Form, Badge } from 'react-bootstrap';
import { Ico35 } from '@icons/new';
import ReusableTable from 'components/reusableTable/reusableTable';
import { getProjectVersions, getProjectAnswers } from '@lib/api';

// Etichetta della versione: la prima fotografia è la valutazione iniziale, quelle
// successive sono gli invii di monitoraggio. `reason` arriva dal backend
// (MONITORING_START / MONITORING_SUBMIT).
const versionLabel = (version, index) => {
  if (index === 0) return 'Valutazione iniziale';
  return version.reason === 'MONITORING_SUBMIT'
    ? `Monitoraggio (invio ${index})`
    : `Versione ${version.ver}`;
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value.replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('it-IT');
};

const answersToText = (answers) => (answers || []).filter(Boolean).join(' · ');

/**
 * Storico delle valutazioni presentate, in sola lettura: per ogni versione le risposte
 * date al questionario, con il confronto rispetto alla valutazione precedente.
 * I risultati (punteggi e rating) restano nella dashboard: qui si vedono le risposte.
 */
const StoricoValutazioni = ({ projectId, translations = [] }) => {
  const [versions, setVersions] = useState([]);
  const [selectedVer, setSelectedVer] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [onlyChanged, setOnlyChanged] = useState(false);

  const title =
    translations.filter((trans) => trans.code === 'ICS208').map((trans) => trans.text)[0] ||
    'Storico valutazioni';

  useEffect(() => {
    if (!projectId) return;
    let mounted = true;

    getProjectVersions(projectId).then((retrieved) => {
      if (!mounted) return;
      setVersions(retrieved);
      // Si apre sull'ultima valutazione presentata.
      setSelectedVer(retrieved.length > 0 ? retrieved[retrieved.length - 1].ver : null);
    });

    return () => {
      mounted = false;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId || selectedVer === null) return;
    let mounted = true;
    setIsLoading(true);

    const index = versions.findIndex((v) => v.ver === selectedVer);
    const previous = index > 0 ? versions[index - 1].ver : null;

    Promise.all([
      getProjectAnswers(projectId, selectedVer),
      previous === null ? Promise.resolve([]) : getProjectAnswers(projectId, previous),
    ])
      .then(([current, before]) => {
        if (!mounted) return;
        const beforeByQuestion = new Map(before.map((q) => [q.questionId ?? q.question_id, q]));

        setRows(
          current.map((q) => {
            const key = q.questionId ?? q.question_id;
            const previousAnswer = beforeByQuestion.get(key);
            const currentText = answersToText(q.answers);
            const previousText = previousAnswer ? answersToText(previousAnswer.answers) : '';
            return {
              category: q.category,
              subcategory: q.subcategory,
              question: q.question,
              answer: currentText,
              previousAnswer: previousText,
              changed: previousAnswer ? currentText !== previousText : false,
              isMonitoring: q.monitoring,
            };
          }),
        );
      })
      .finally(() => mounted && setIsLoading(false));

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, selectedVer, versions]);

  const visibleRows = useMemo(
    () => (onlyChanged ? rows.filter((row) => row.changed) : rows),
    [rows, onlyChanged],
  );

  const hasComparison = versions.findIndex((v) => v.ver === selectedVer) > 0;
  const changedCount = rows.filter((row) => row.changed).length;

  const columnDefs = useMemo(() => {
    const columns = [
      { headerName: 'Area', field: 'category', width: 90, filter: 'agTextColumnFilter' },
      { headerName: 'Ambito', field: 'subcategory', width: 110, filter: 'agTextColumnFilter' },
      {
        headerName: 'Domanda',
        field: 'question',
        flex: 2,
        wrapText: true,
        autoHeight: true,
        tooltipField: 'question',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Risposta',
        field: 'answer',
        flex: 1,
        wrapText: true,
        autoHeight: true,
        cellStyle: (params) =>
          params.data?.changed ? { color: '#08a2aa', fontWeight: 'bold' } : null,
      },
    ];

    if (hasComparison) {
      columns.push({
        headerName: 'Risposta precedente',
        field: 'previousAnswer',
        flex: 1,
        wrapText: true,
        autoHeight: true,
        cellStyle: () => ({ color: '#6c757d' }),
      });
    }

    return columns;
  }, [hasComparison]);

  if (versions.length === 0) return null;

  return (
    <Card className='cards-utente mt-4'>
      <Card.Body>
        <Ico35 className='logo-card ico-box-blue' />
        <Card.Title className='margin-title-box text-uppercase pt-3'>{title}</Card.Title>

        <Row className='align-items-center justify-content-between mb-3'>
          <Col xs={12} md={6} className='d-flex align-items-center gap-2'>
            <Form.Label htmlFor='storico-versione' className='m-0 mr-2'>
              Valutazione
            </Form.Label>
            <Form.Control
              id='storico-versione'
              as='select'
              value={selectedVer ?? ''}
              onChange={(e) => setSelectedVer(Number(e.target.value))}
              style={{ maxWidth: '22rem' }}>
              {versions.map((version, index) => (
                <option key={version.ver} value={version.ver}>
                  {versionLabel(version, index)} — {formatDate(version.createdAt ?? version.created_at)}
                </option>
              ))}
            </Form.Control>
          </Col>

          {hasComparison && (
            <Col xs={12} md={6} className='d-flex align-items-center justify-content-md-end mt-2 mt-md-0'>
              <Badge variant='info' className='mr-3'>
                {changedCount} risposte modificate
              </Badge>
              <Form.Check
                type='checkbox'
                id='storico-solo-modificate'
                label='Mostra solo le modificate'
                checked={onlyChanged}
                onChange={(e) => setOnlyChanged(e.target.checked)}
              />
            </Col>
          )}
        </Row>

        {isLoading ? (
          <div className='d-flex justify-content-center align-items-center p-4'>
            <Spinner animation='border' role='status' />
            <span className='ml-2'>Caricamento…</span>
          </div>
        ) : (
          <ReusableTable columnDefs={columnDefs} rowData={visibleRows} />
        )}
      </Card.Body>
    </Card>
  );
};

export default StoricoValutazioni;
